'use client';

import Link from 'next/link';
import {
  ArrowRight,
  ArrowUpRight,
  Languages,
  ScanFace,
  UserRound,
  type LucideIcon
} from 'lucide-react';
import { motion } from 'motion/react';

import { routes } from '@workspace/routes';
import { Button } from '@workspace/ui/components/customButton';

import { trackCta } from '~/lib/track-cta';

type AvatarCard = {
  icon: LucideIcon;
  title: string;
  description: string;
  href: string;
  linkLabel: string;
};

const cards: AvatarCard[] = [
  {
    icon: UserRound,
    title: 'An AI presenter, not a talking-head video',
    description:
      'Pick a presenter from the catalog and they deliver your intro, outro, and transitions — your screen recording still carries the demo in between.',
    href: routes.marketing.tools.AiAvatarVideoMaker,
    linkLabel: 'AI avatar video maker'
  },
  {
    icon: ScanFace,
    title: 'Clone yourself in minutes',
    description:
      'One photo gives you a photo avatar in minutes. For a full digital twin, record 15 seconds to 10 minutes of footage plus a short consent statement — then reuse up to 10 looks of the same person.',
    href: routes.marketing.tools.AiVideoClone,
    linkLabel: 'AI video clone'
  },
  {
    icon: Languages,
    title: 'Speak every language',
    description:
      'Publish the same video in 70+ languages. Each one is a full re-render — translated script, native AI voiceover, and captions — not a dub laid over the original.',
    href: routes.marketing.tools.AiVideoTranslator,
    linkLabel: 'AI video translator'
  }
];

export default function AvatarFeatureSection() {
  return (
    <section
      id="avatars"
      className="py-14 sm:py-20 md:py-24 bg-background"
    >
      <div className="mx-auto max-w-6xl px-4 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 sm:mb-14"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 px-3 py-1 text-xs font-medium text-primary mb-4 bg-videomule-light-green dark:bg-primary/10">
            <UserRound className="h-3.5 w-3.5" />
            AI Avatars
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground">
            Put a presenter in your video — even you
            <span className="text-primary">.</span>
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto text-sm sm:text-base md:text-lg leading-relaxed">
            Train an AI clone from 15 seconds of footage, or pick a stock
            presenter. The avatar delivers your intro, outro, and transitions
            while your screen recording stays the star.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <Link
                  href={card.href}
                  className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 sm:p-6 hover:border-primary/30 hover:shadow-lg transition-all duration-300"
                >
                  <div className="shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-base sm:text-lg text-foreground leading-snug mb-2">
                    {card.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {card.description}
                  </p>
                  <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                    {card.linkLabel}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Plan facts + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-6 sm:mt-8 rounded-2xl border border-border bg-card p-5 sm:p-6 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 flex-1">
            <div>
              <p className="text-sm font-semibold text-foreground">
                Normal, Standard, High
              </p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Three quality tiers, all rendered at 1080p — pick fidelity per
                video and pay 1×, 3×, or 4× avatar minutes.
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                3, 8, or 20 avatar minutes
              </p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Included every month on Creator, Team, and Agency — top up
                anytime
                with credit packs.
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                1, 3, or 10 avatar slots
              </p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Each slot is one person, with up to 10 looks. Avatars unlock
                when your paid plan starts.
              </p>
            </div>
          </div>
          <Button
            href={routes.dashboard.auth.SignUp}
            onClick={() =>
              trackCta('cta_signup_clicked', { surface: 'homepage_avatars' })
            }
            className="group w-fit shrink-0 gap-2 font-semibold hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
          >
            Start Free Trial
            <ArrowRight className="h-4 w-4" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
